import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../../redux/auth/selectors';
import { mainColors } from '../../../../../styling/theme';
import CustomTable from '../../../../tables/CustomTable';
import type {
  CssfCounterpartyInscopeRow,
  CssfInscopePositionRow,
  CssfInscopePositionsResponse,
  CssfRiskSpreadingFund,
} from '../../../../../api/rs-hg/risk/cssfRiskSpreading.types';
import { useCssfInscopePositionsQuery } from '../../../../../api/rs-hg/risk/hooks/queries';
import { QueryBoundary } from '../../../../utilityComponents/QueryBoundary';
import {
  CounterpartyDetailData,
  CssfDetailDataDefault,
  generateCounterpartyDetailColumns,
  generateCounterpartyDetailData,
  generateCssfDetailColumns,
  generateCssfDetailData,
} from './CssfDetail.data';
import { useTheme } from '@mui/material';

type DetailProps = {
  rule: string;
  fundName: string;
  rows: CssfInscopePositionsResponse;
};

export const CssfDetailTableCounterparty = ({
  fundName,
  rule,
  rows,
}: DetailProps) => {
  const columns = generateCounterpartyDetailColumns();
  const tableData = generateCounterpartyDetailData(
    rows as Array<CssfCounterpartyInscopeRow | string[]>,
  );
  return (
    <CustomTable<CounterpartyDetailData>
      options={{
        exportButton: true,
        search: true,
        paging: false,
      }}
      data={tableData}
      columns={columns}
      showToolbar
      title={`${fundName} - ${rule}`}
    />
  );
};

export const CssfDetailTableDefault = ({
  fundName,
  rule,
  rows,
}: DetailProps): ReactElement => {
  const clientName = useSelector(clientNameSelector);
  const columns = generateCssfDetailColumns();
  const detailData = generateCssfDetailData(
    rows as CssfInscopePositionRow[],
    clientName || 'mersenne',
  );

  const theme = useTheme();
  return (
    <CustomTable<CssfDetailDataDefault>
      options={{
        paging: false,
        exportButton: true,
        rowStyle: () => ({
          backgroundColor: theme.palette.grey[100],
        }),
      }}
      toolbarComponents={{
        titleStyle: {
          fontSize: '1.25rem',
          fontWeight: 400,
          color: mainColors.mainGold,
        },
        toolbarTitle: `${rule}`,
      }}
      title={`${rule}: ${fundName}`}
      showToolbar={true}
      data={detailData}
      columns={columns}
      id={`pdf_${fundName}_${rule}`}
    />
  );
};

interface CssfDetailProps {
  fund: CssfRiskSpreadingFund;
  positionDate?: string;
  ruleNumber: number;
  isCounterparty: boolean;
  ruleTitle: string;
}

const CssfDetailComponent = (props: CssfDetailProps) => {
  const { positionDate, ruleNumber, fund, ruleTitle, isCounterparty } = props;
  const fundName = fund.fund_name;
  const displayName = fund.fund_name_full || fund.fund_name;

  const { data, isLoading, isFetching, isError, error } =
    useCssfInscopePositionsQuery(fundName, ruleNumber, positionDate);

  return (
    <QueryBoundary<CssfInscopePositionsResponse>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={data}
      card
      loadingMessages={['Loading CSSF detail...']}
      errorMessage="No detail data available."
      noDataMessage="No detail data available."
    >
      {(rows) =>
        isCounterparty ? (
          <CssfDetailTableCounterparty
            rows={rows}
            rule={ruleTitle}
            fundName={displayName}
          />
        ) : (
          <CssfDetailTableDefault
            rows={rows}
            rule={ruleTitle}
            fundName={displayName}
          />
        )
      }
    </QueryBoundary>
  );
};

export default CssfDetailComponent;
